import React, { useCallback, useContext, useState } from 'react';
import t from 'react-translate';
import NvDropdown, {
  NvDropdownAlign, NvDropdownButtonStyle, NvDropdownOption,
} from 'shared/components/inputs/nv-dropdown';
import NvIcon from 'shared/components/nv-icon';
import NvTooltip from 'shared/components/nv-tooltip';
import { AngularContext } from 'react-app';
import { useAppDispatch } from 'redux/store';
import { toggleSftpReport } from 'redux/actions/custom-downloads';
import ClickableContainer from 'components/clickable-container';
import DailySftpSwitch from './daily-sftp-switch';

type DownloadsCardOptionsProps = {
  editReport: () => void
  duplicateReport: () => void
  deleteReport: () => void
  toggleSftpReport?: () => void
  reportId?: number
};

const DownloadsCardOptions: React.FC<DownloadsCardOptionsProps> = ({
  editReport,
  duplicateReport,
  deleteReport,
  reportId,
}) => {
  const { injectServices } = useContext(AngularContext);
  const [$state, $timeout, CustomDownloadsManager] = injectServices(['$state', '$timeout', 'CustomDownloadsManager']);

  const getDownloads = useCallback(() => {
    const data = CustomDownloadsManager?.data || {};
    const dataDownload = (data.dataDownload || []).map(item => ({ ...item, type: 'dataDownload' }));
    const contentDownload = (data.contentDownload || []).map(item => ({ ...item, type: 'contentDownload' }));
    return [...dataDownload, ...contentDownload];
  }, [CustomDownloadsManager?.data]);

  const downloads = getDownloads();
  const downloadInfo = downloads.find(item => item.reportId === reportId) || {};
  const { type, isDailySftp } = downloadInfo;
  const showSftpToggle = CustomDownloadsManager?.data?.showSftpToggle;

  const [hasDailySftpEnabled, setHasDailySftpEnabled] = useState(isDailySftp);
  const [isRequesting, setIsRequesting] = useState(false);

  const dispatch = useAppDispatch();

  const onToggleSftpReport = () => {
    setIsRequesting(true);
    dispatch(toggleSftpReport({
      catalogId: $state.params.catalogId,
      reportId,
    }))
      .then(() => {
        $timeout(() => {
          CustomDownloadsManager.toggleSftpReport(reportId, !hasDailySftpEnabled);
        });
        setIsRequesting(false);
        setHasDailySftpEnabled(prev => !prev);
      });
  };

  let options: NvDropdownOption[];
  const deleteOption: NvDropdownOption = {
    type: 'text',
    text: t.FORM.DELETE(),
    class: 'text-danger',
    callback: deleteReport,
  };
  if (type === 'dataDownload') {
    const dataDownloadoptions: NvDropdownOption[] = [
      {
        type: 'custom',
        customItem: (
          <ClickableContainer
            className={`d-flex gray-1 px-4 py-1 align-items-center ${hasDailySftpEnabled ? 'gray-4' : ''}`}
            onClick={editReport}
            disabled={hasDailySftpEnabled}
          >
            {t.FORM.EDIT()}
            {hasDailySftpEnabled && (
              <NvTooltip text={t.CUSTOM_DOWNLOADS.SFTP_INFO()}>
                <NvIcon
                  icon='info'
                  size='xs-smallest'
                  className=' ml-1 text-primary'
                />
              </NvTooltip>
            )}
          </ClickableContainer>),
        disabled: hasDailySftpEnabled,
      },
      {
        type: 'text',
        text: t.CUSTOM_DOWNLOADS.DUPLICATE(),
        callback: duplicateReport,
        class: 'text-gray-1',
      },
      {
        type: 'divider',
      },
    ];

    if (showSftpToggle) {
      dataDownloadoptions.push(
        isRequesting ? {
          type: 'text',
          text: hasDailySftpEnabled ? t.CUSTOM_DOWNLOADS.SFTP_DISABLING() : t.CUSTOM_DOWNLOADS.SFTP_ENABLING(),
          disabled: true,
        } : {
          type: 'custom',
          customItem: (
            <DailySftpSwitch
              isDailysftp={hasDailySftpEnabled}
              toggleSftpReport={onToggleSftpReport}
            />
          ),
        },
        {
          type: 'divider',
        },
      );
    }

    options = [...dataDownloadoptions, deleteOption];
  } else {
    options = [deleteOption];
  }

  return (
    <div className={`${type === 'dataDownload' && showSftpToggle ? 'data-menu' : ''} course-options`}>
      <NvDropdown
        items={options}
        buttonStyle={NvDropdownButtonStyle.ICON}
        iconClass='icon-smallest icon-settings gray-3'
        align={NvDropdownAlign.RIGHT}
        menuClassName='dropdown-menu'
      />
    </div>
  );
};

export default DownloadsCardOptions;
